<script setup lang="ts">
import type { AddressFragment } from '#graphql-operations'

const { t } = useI18n()

const title = computed(() => t('layouts.title', [t('addresses.page.title')]))

useSeoMeta({
  title,
  ogTitle: title,
})

const showAddressForm = ref(false)

const { data: activeCustomerAddresses, refresh: refreshAddresses } = await useLazyAsyncData('activeCustomerAddresses', async () => {
  const { data } = await useGraphqlQuery('activeCustomerAddresses')
  const addresses = (data.activeCustomer?.addresses ?? []) as AddressFragment[]
  // sort by created at, and then by default shipping/billing address
  return addresses.sort((a, b) => {
    if (a.defaultShippingAddress && !b.defaultShippingAddress)
      return -1
    if (!a.defaultShippingAddress && b.defaultShippingAddress)
      return 1
    if (a.defaultBillingAddress && !b.defaultBillingAddress)
      return -1
    if (!a.defaultBillingAddress && b.defaultBillingAddress)
      return 1
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
})

async function onOpen(isOpen: boolean) {
  if (showAddressForm.value)
    await refreshAddresses()
  showAddressForm.value = isOpen
}
</script>

<template>
  <UiCard class="border-none shadow-none">
    <UiCardHeader class="block flex-row sm:flex sm:items-baseline sm:justify-between">
      <div class="flex flex-col gap-y-1.5 sm:w-0 sm:flex-1">
        <UiCardTitle>{{ t('addresses.page.title') }}</UiCardTitle>
        <UiCardDescription>{{ t('addresses.page.description') }}</UiCardDescription>
      </div>

      <div class="mt-4 flex items-center justify-between sm:ml-6 sm:mt-0 sm:flex-shrink-0 sm:justify-start">
        <UiDialog v-model:open="showAddressForm" @update:open="onOpen">
          <UiDialogTrigger as-child>
            <UiButton
              variant="orange"
              class="rounded-full"
            >
              {{ t('addresses.page.add_address') }}
            </UiButton>
          </UiDialogTrigger>
          <UiDialogContent>
            <UiDialogHeader>
              <UiDialogTitle>
                {{ t('addresses.page.add_address') }}
              </UiDialogTitle>
            </UiDialogHeader>
            <LazyCustomerAddressForm
              v-if="showAddressForm"
              @update:address="onOpen(false)"
              @error="onOpen(false)"
            />
          </UiDialogContent>
        </UiDialog>
      </div>
    </UiCardHeader>

    <UiCardContent>
      <div class="grid gap-4 md:grid-cols-2">
        <template v-if="activeCustomerAddresses?.length">
          <LazyAddressCard
            v-for="address in activeCustomerAddresses" :key="address.id"
            :address="address"
            @delete:address="() => refreshAddresses()"
            @update:address="() => refreshAddresses()"
          />
        </template>
      </div>
    </UiCardContent>
  </UiCard>
</template>
